/** Shared embed widget URL + iframe snippet helpers (Brands builder + /embed page). */

export const EMBED_RANGE_KEYS = ['1M', '3M', '6M', 'YTD', '1Y', 'All'];
export const EMBED_BENCHMARK_TICKERS = ['SPY', 'QQQ', 'IEF', 'GLD', 'ES', 'NQ'];
export const EMBED_BENCHMARK_LABELS = {
  SPY: 'S&P 500 (SPY)',
  QQQ: 'Nasdaq-100 (QQQ)',
  IEF: 'US 7-10Y Treasury (IEF)',
  GLD: 'Gold (GLD)',
  ES: 'E-mini S&P 500 (ES)',
  NQ: 'E-mini Nasdaq-100 (NQ)',
};
export const EMBED_DEFAULT_BENCHMARKS = ['SPY', 'GLD', 'QQQ'];
export const EMBED_DEFAULT_STRATEGY_COLORS = ['#2563eb', '#8b5cf6', '#ec4899', '#22c55e', '#f59e0b', '#06b6d4'];
export const EMBED_MAX_STRATEGIES = 6;

export const DEFAULT_EMBED_WIDGET = {
  strategies: [],
  benchmarks: [...EMBED_DEFAULT_BENCHMARKS],
  range: 'All',
  capital: 100000,
  colors: [],
  theme: 'light',
  bg: 'f8fcff',
  controls: true,
  title: '',
  badge: 'Live trading since 2022',
  disclaimer: '',
  allowedDomains: [],
};

export function parseHexColor(raw) {
  const v = String(raw || '').trim().replace(/^#/, '');
  return /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : null;
}

export function parseBenchmarkToken(token) {
  const t = String(token || '').trim();
  if (!t) return null;
  const paren = t.match(/\(([A-Za-z]{1,6})\)\s*$/);
  const candidate = (paren ? paren[1] : t).toUpperCase();
  return EMBED_BENCHMARK_TICKERS.includes(candidate) ? candidate : null;
}

/** Hostname only — no protocol, path, or port. */
export function normalizeEmbedDomain(raw) {
  let s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  s = s.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(s)) return '';
  return s;
}

export function normalizeEmbedWidget(input = {}) {
  const src = input && typeof input === 'object' ? input : {};
  const strategies = (Array.isArray(src.strategies) ? src.strategies : [])
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .slice(0, EMBED_MAX_STRATEGIES);
  const benchmarks = Array.from(new Set(
    (Array.isArray(src.benchmarks) ? src.benchmarks : EMBED_DEFAULT_BENCHMARKS)
      .map(parseBenchmarkToken)
      .filter(Boolean),
  ));
  const rangeRaw = String(src.range || 'All').trim();
  const range = EMBED_RANGE_KEYS.find((r) => r.toLowerCase() === rangeRaw.toLowerCase()) || 'All';
  const capitalRaw = Number(src.capital);
  const capital = Number.isFinite(capitalRaw) && capitalRaw >= 1000 && capitalRaw <= 100000000
    ? capitalRaw
    : 100000;
  const colors = (Array.isArray(src.colors) ? src.colors : [])
    .map(parseHexColor)
    .filter(Boolean);
  const theme = String(src.theme || 'dark').trim().toLowerCase() === 'light' ? 'light' : 'dark';
  const bg = parseHexColor(src.bg) || 'f8fcff';
  const controls = src.controls !== false && String(src.controls) !== '0';
  const trimText = (v, max) => String(v ?? '').trim().slice(0, max);
  const allowedDomains = Array.from(new Set(
    (Array.isArray(src.allowedDomains) ? src.allowedDomains : [])
      .map(normalizeEmbedDomain)
      .filter(Boolean),
  ));
  const entityRaw = String(src.entity || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
  const entity = entityRaw || null;
  return {
    strategies,
    benchmarks: benchmarks.length ? benchmarks : [...EMBED_DEFAULT_BENCHMARKS],
    range,
    capital,
    colors,
    theme,
    bg,
    controls,
    title: trimText(src.title, 60),
    badge: src.badge === null || src.badge === false ? '' : trimText(src.badge ?? DEFAULT_EMBED_WIDGET.badge, 60),
    disclaimer: trimText(src.disclaimer, 300),
    allowedDomains,
    entity,
  };
}

export function parseEmbedConfig(search) {
  const query = search != null && String(search).length > 0
    ? String(search).replace(/^\?/, '')
    : (typeof window !== 'undefined' ? window.location.search.replace(/^\?/, '') : '');
  const params = new URLSearchParams(query);
  const list = (key) => String(params.get(key) || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const entity = String(params.get('entity') || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '') || null;

  const strategies = list('strategies').slice(0, EMBED_MAX_STRATEGIES);
  const benchmarkOptions = Array.from(new Set(list('benchmarks').map(parseBenchmarkToken).filter(Boolean)));

  const rangeRaw = String(params.get('range') || 'All').trim();
  const range = EMBED_RANGE_KEYS.find((r) => r.toLowerCase() === rangeRaw.toLowerCase()) || 'All';

  const capitalRaw = Number(params.get('capital'));
  const capital = Number.isFinite(capitalRaw) && capitalRaw >= 1000 && capitalRaw <= 100000000
    ? capitalRaw
    : 100000;

  const colors = list('colors').map((c) => {
    const hex = parseHexColor(c);
    return hex ? `#${hex}` : null;
  }).filter(Boolean);
  const theme = String(params.get('theme') || 'dark').trim().toLowerCase() === 'light' ? 'light' : 'dark';
  const bgOverride = theme === 'light' ? parseHexColor(params.get('bg')) : null;
  const controls = String(params.get('controls') || '1') !== '0';

  const textParam = (key, fallback) => {
    if (!params.has(key)) return fallback;
    return String(params.get(key) || '').trim().slice(0, 60) || null;
  };
  const badge = textParam('badge', DEFAULT_EMBED_WIDGET.badge);
  const title = textParam('title', 'RiskLo performance dashboard');
  const disclaimer = !params.has('disclaimer')
    ? 'RiskLo chart data is for informational purposes only. Past performance, whether backtested or live, does not guarantee future results. All trading involves risk of loss.'
    : (String(params.get('disclaimer') || '').trim().slice(0, 300) || null);

  return {
    entity,
    strategies,
    benchmarkOptions: benchmarkOptions.length ? benchmarkOptions : [...EMBED_DEFAULT_BENCHMARKS],
    range,
    capital,
    colors,
    theme,
    bgOverride: bgOverride ? `#${bgOverride}` : null,
    controls,
    badge,
    title,
    disclaimer,
  };
}

export function buildEmbedUrl(widget = {}, { origin = 'https://risklo.io' } = {}) {
  const cfg = normalizeEmbedWidget(widget);
  const params = new URLSearchParams();
  if (cfg.entity) params.set('entity', cfg.entity);
  if (cfg.strategies.length) params.set('strategies', cfg.strategies.join(','));
  if (cfg.benchmarks.length) params.set('benchmarks', cfg.benchmarks.join(','));
  params.set('range', cfg.range);
  params.set('capital', String(cfg.capital));
  if (cfg.colors.length) params.set('colors', cfg.colors.join(','));
  params.set('theme', cfg.theme);
  if (cfg.theme === 'light' && cfg.bg) params.set('bg', cfg.bg);
  if (!cfg.controls) params.set('controls', '0');
  if (cfg.title) params.set('title', cfg.title);
  if (cfg.badge) params.set('badge', cfg.badge);
  if (cfg.disclaimer) params.set('disclaimer', cfg.disclaimer);
  const base = String(origin || '').replace(/\/$/, '') || 'https://risklo.io';
  return `${base}/embed?${params.toString()}`;
}

export function buildEmbedIframeSnippet(widget = {}, opts = {}) {
  const src = buildEmbedUrl(widget, opts);
  return [
    `<iframe`,
    `  src="${src}"`,
    `  title="RiskLo performance chart"`,
    `  width="100%"`,
    `  height="520"`,
    `  style="border:0; max-width:100%; min-height:480px;"`,
    `  loading="lazy"`,
    `  allow="fullscreen"`,
    `></iframe>`,
  ].join('\n');
}
